import React, { useLayoutEffect } from "react";

import { ActionsProvider } from "../../../../providers/ActionsProvider/ActionsProvider";
import { TooltipProvider } from "../../../../providers/TooltipProvider/TooltipProvider";

import {
  fullHeightContainer,
  removeFullHeightContainer,
} from "../../../../utilities/helpers";
import {
  getVenueSeatScale,
  isNmsuStyleSeatmapVenue,
} from "../../../../utilities/frontendSeatmapSizing";

import { SeatMap } from "../../../SeatMap";

export default function ExchangeTicketsMap({
  data,
  background,
  selectedSeatIds,
  setSelectedSeatIds,
  isMapDisabled,
  soldMap,
  openGAModal,
  event,
}) {
  const SELECTED_COLOR = "#141416"; // black - selecting seats on map
  const SOLD_COLOR = "#45B26B"; // green for sold
  const DEFAULT_COLOR = "#c6c8cc";
  const venueSlug = event?.venue?.slug || event?.venueSlug || event?.venue_slug;
  const venueName = event?.venue?.name || event?.venueName || event?.venue_name;
  const useFrontendSeatSizing = isNmsuStyleSeatmapVenue(venueSlug);
  const seatScale = getVenueSeatScale({ venueName, venueSlug });

  // Hides default navigation
  useLayoutEffect(() => {
    // hide navbar
    const nav = document.getElementById("navbar");
    if (nav) nav.style.display = "none";
    // hide event banner
    const banner = document.getElementById("banner");
    if (banner) banner.style.display = "none";
    // hide sidebar
    const sidebarNav = document.getElementById("sidebarMenu");
    if (sidebarNav) sidebarNav.style.display = "none";
    // modify container
    const container = document.getElementById("main-container");
    fullHeightContainer(container);
    const content = document.getElementById("main-content");
    if (content) {
      content.classList.add("pb-0");
      content.classList.add("full-height-wrapper");
      content.classList.remove("spacer-md", "spacer-md--with-banner");
    }
    return () => {
      if (nav) nav.style.display = "";
      if (banner) banner.style.display = "";
      if (sidebarNav) sidebarNav.style.display = "";
      removeFullHeightContainer(container);
      if (content) {
        content.classList.remove("pb-0");
        content.classList.remove("full-height-wrapper");
        content.classList.add("spacer-md", "spacer-md--with-banner");
      }
    };
  }, []);

  // save regular seated seats,,,
  const selectRegularSeats = (seatId, seatIdArray) => {
    // if map is disabled don't select seats
    if (isMapDisabled) return;

    setSelectedSeatIds((prevSelected) => {
      const newSelected = { ...prevSelected };
      if (!newSelected.seats) {
        newSelected.seats = {};
      }

      const addSeat = (id) => {
        if (!getSeatState(id).disabled) {
          if (newSelected.seats[id] === id) {
            delete newSelected.seats[id];
          } else {
            newSelected.seats[id] = id;
          }
        }
      };

      if (seatId) {
        // Handle single seat selection
        addSeat(seatId);
      } else if (seatIdArray) {
        // Handle multiple seat selection
        seatIdArray.forEach(addSeat);
      }

      // If empty dont return .seats
      if (Object.keys(newSelected.seats).length === 0) {
        delete newSelected.seats;
      }

      return newSelected;
    });
  };

  // GA sections use this to get their color
  const getSectionState = (section) => {
    if (
      selectedSeatIds &&
      selectedSeatIds.gaSeats &&
      selectedSeatIds.gaSeats.hasOwnProperty(section.sectionId)
    ) {
      return { disabled: false, color: SELECTED_COLOR };
    }

    return { disabled: false, color: DEFAULT_COLOR };
  };

  // for sold seats return sold color + disabled
  const getSeatState = (seatId) => {
    if (soldMap?.get(seatId)) {
      return { disabled: true, color: SOLD_COLOR };
    }

    if (selectedSeatIds?.seats && selectedSeatIds.seats[seatId] === seatId) {
      return { disabled: false, color: SELECTED_COLOR };
    }

    return { disabled: false, color: DEFAULT_COLOR };
  };

  // This is triggered for BOTH GA and seated:
  // ( since you can select seated by selecting the entire section )
  const handleSectionSelect = (section) => {
    // if map is disabled don't select sections
    if (isMapDisabled) return;

    if (section?.zoomable) {
      // Seated-section
      const seatIds = getAllSeatsInSeatedSection(
        section,
        data.rows,
        data.seats,
      );
      selectRegularSeats(undefined, seatIds);
    } else {
      // GA-section
      openGAModal(section);
    }
  };

  const getAllSeatsInSeatedSection = (section, rows, seats) => {
    return Object.values(rows)
      .filter((row) => row.sectionId === section.sectionId)
      .flatMap((row) => row.seats)
      .filter((seatId) => {
        if (!seats[seatId]) return false;
        return true;
      });
  };

  const legendItems = [
    { label: "Sold", className: "sold" },
    { label: "Selected", className: "selected" },
    { label: "Unselected", className: "unselected" },
  ];

  // Lets not show anything for now ( since the packages assign map does not either )
  const inventoryInfoBasedOnMapType = {
    displayInventoryInfo: false,
    deriveInventoryInfoFrom: [],
  };

  /*
    Customize this file ( or the files above ) in any which way that suits your purposes
    Just DO-NOT customize anything lower than this
  */

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {/* Seatmap fills all available space, so its also behind the Navigation now */}
      <TooltipProvider>
        <ActionsProvider canEdit={!isMapDisabled} legendItems={legendItems}>
          <SeatMap
            data={data}
            background={background}
            isMapDisabled={isMapDisabled}
            useFrontendSeatSizing={useFrontendSeatSizing}
            seatScale={seatScale}
            // Handling clicks
            selectRegularSeats={selectRegularSeats}
            handleSectionSelect={handleSectionSelect}
            // Fill Colors
            getSectionState={getSectionState}
            getSeatState={getSeatState}
            // For inventory "bar"
            displayInventoryInfo={
              inventoryInfoBasedOnMapType.displayInventoryInfo
            }
            deriveInventoryInfoFrom={
              inventoryInfoBasedOnMapType.deriveInventoryInfoFrom
            }
          />
        </ActionsProvider>
      </TooltipProvider>
    </div>
  );
}
